import { CATALOG } from '../core/vessel/catalog'
import { altitudeOf } from '../core/dynamics/flight'
import { fuelPathForEngine, availablePropellant } from '../core/vessel/propellant'
import { orbitReadouts, surfaceVelocity, verticalSpeed, dynamicPressure } from '../core/nav/readouts'
import { attitudeOf } from '../core/nav/attitude'
import { length } from '../core/math/vec3'
import { Simulation, SimVessel } from '../game/simulation'
import { stagesOf } from '../game/staging'
import { Autopilot } from '../game/autopilot'
import { distanza, velocita, tempo, pressione, percento, gradi, massa } from './format'

const RIGHE: Array<[string, string]> = [
  ['quota', 'quota'],
  ['velocita-superficie', 'vel. suolo'],
  ['velocita-orbitale', 'vel. orbitale'],
  ['velocita-verticale', 'vel. verticale'],
  ['pressione', 'pressione din.'],
  ['struttura', 'carico strutt.'],
  ['temperatura', 'temperatura'],
  ['apoapside', 'apoapside'],
  ['periapside', 'periapside'],
  ['tempo-apoapside', 't. apoapside'],
  ['massa', 'massa'],
  ['propellente', 'propellente'],
  ['manetta', 'manetta'],
  ['assetto', 'assetto'],
  ['stadio', 'stadio'],
  ['paracadute', 'paracadute'],
  ['pilota', 'pilota'],
]

const SEPARATORI = new Set(['apoapside', 'massa'])

export interface Hud {
  update(sim: Simulation, sv: SimVessel, ap: Autopilot): void
}

export function createHud(root: HTMLElement): Hud {
  root.innerHTML = ''
  const titolo = document.createElement('h1')
  titolo.textContent = 'Apogeo'
  root.appendChild(titolo)

  const celle = new Map<string, HTMLElement>()
  let dl = document.createElement('dl')
  root.appendChild(dl)

  for (const [campo, etichetta] of RIGHE) {
    if (SEPARATORI.has(campo)) {
      dl = document.createElement('dl')
      dl.className = 'gruppo'
      root.appendChild(dl)
    }
    const dt = document.createElement('dt')
    dt.textContent = etichetta
    const dd = document.createElement('dd')
    dd.dataset.campo = campo
    dd.textContent = '—'
    dl.append(dt, dd)
    celle.set(campo, dd)
  }

  const barra = document.createElement('div')
  barra.className = 'barra'
  const riempimento = document.createElement('i')
  barra.appendChild(riempimento)
  root.appendChild(barra)

  const scrivi = (campo: string, testo: string, classe = ''): void => {
    const c = celle.get(campo)!
    if (c.textContent !== testo) c.textContent = testo
    if (c.className !== classe) c.className = classe
  }

  return {
    update(sim, sv, ap) {
      const q = altitudeOf(sv.state.position, sim.body)
      const ro = orbitReadouts(sv.state, sim.body)
      const att = attitudeOf(sv.state.position, sv.state.orientation)
      const pd = dynamicPressure(sv.state, sim.body)

      // a motori spenti si mostra il propellente dello stadio che si accendera
      // col prossimo comando: dire "0 kg" su un razzo pieno e una bugia
      const motori = sv.activeEngines.length > 0
        ? sv.activeEngines
        : (stagesOf(sv.vessel, CATALOG).find((s) => s.stage === sim.nextStage)?.engines ?? [])
      const serbatoi = new Set<string>()
      for (const e of motori) {
        if (sv.vessel.parts.some((p) => p.id === e)) {
          for (const t of fuelPathForEngine(sv.vessel, CATALOG, e)) serbatoi.add(t)
        }
      }
      const carburante = availablePropellant(sv.vessel, [...serbatoi])
      const accesi = sv.activeEngines.length > 0

      scrivi('quota', distanza(q))
      scrivi('velocita-superficie', velocita(length(surfaceVelocity(sv.state, sim.body))))
      scrivi('velocita-orbitale', velocita(length(sv.state.velocity)))
      scrivi('velocita-verticale', velocita(verticalSpeed(sv.state, sim.body)))
      // oltre i 25 kPa la struttura soffre e il gimbal perde autorita
      scrivi('pressione', pressione(pd), pd > 25_000 ? 'allarme' : pd > 12_000 ? 'acceso' : '')
      scrivi('apoapside', distanza(ro.apoapsis), ro.apoapsis > 70_000 ? 'buono' : '')
      scrivi('periapside', distanza(ro.periapsis), ro.periapsis > 70_000 ? 'buono' : '')
      scrivi('tempo-apoapside', tempo(ro.timeToApoapsis))
      scrivi('massa', massa(sv.massProps.mass))
      scrivi('propellente', massa(carburante), carburante <= 0 ? 'allarme' : accesi ? '' : 'tenue')
      scrivi('manetta', percento(sv.controls.throttle), sv.controls.throttle > 0 ? 'acceso' : '')
      scrivi('assetto', `${gradi(att.pitch)} / ${gradi(att.heading)}`)
      // carico strutturale: sopra 0.8 il razzo e a un passo dallo spezzarsi
      const carico = sv.caricoStrutturale
      scrivi('struttura', `${Math.round(carico * 100)}%`,
        carico > 0.8 ? 'allarme' : carico > 0.5 ? 'acceso' : '')

      const temperature = Object.values(sv.termico.temperature)
      const tMax = temperature.length ? Math.max(...temperature) : 0
      scrivi('temperatura', `${Math.round(tMax)} K`,
        tMax > 900 ? 'allarme' : tMax > 500 ? 'acceso' : '')

      scrivi('stadio', `${sim.nextStage} di 2`)

      const stati = sv.paracadute.map((x) => x.stato)
      scrivi('paracadute',
        stati.length === 0 ? '—'
          : stati.includes('strappato') ? 'STRAPPATO'
            : stati.includes('aperto') ? 'aperto'
              : stati.includes('parziale') ? 'in apertura' : 'chiuso',
        stati.includes('strappato') ? 'allarme' : stati.includes('aperto') ? 'buono' : '')
      scrivi('pilota', ap.mode)

      riempimento.style.width = `${Math.round(sv.controls.throttle * 100)}%`
    },
  }
}
